import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useOrderFullscreenModalLayoutFix } from '../../hooks/order/useOrderFullscreenModalLayoutFix';
import StatisticsCount from '../statistics/common/StatisticsCount';
import DashboardChartRefreshModeBadge from '../statistics/charts/board/ui/DashboardChartRefreshModeBadge';
import DashboardChartView from '../statistics/charts/board/ui/DashboardChartView';
import ChartExplorerOrdersTable from './charts-library/ChartExplorerOrdersTable';
import { isChartExplorerQueryLoading } from './charts-library/chartExplorerDetailUtils';
import { resolvePersistedChartIsMapPreview, resolvePersistedChartKind, resolvePersistedChartTypeLabel, PERSISTED_CHART_KIND, buildPersistedChartFilters } from '../statistics/charts/board/lib/statisticsPersistedCharts';
import canDashboardChartDrillOrders, {
	resolveDashboardChartDrillEmptyMessage,
	resolveSavedChartDrillDisabledReason,
} from './lib/canDashboardChartDrillOrders';
import { STATISTICS_EMPTY_DRILL_ORDERS } from '../statistics/common/lib/statisticsEmptyStateCopy';
import filterHomeDashboardChartDrillOrders from './lib/filterHomeDashboardChartDrillOrders';
import filterLivePresetDrillOrders from './lib/filterLivePresetDrillOrders';
import { formatLineCompareDrillOrdersAmount, formatLineCompareDrillTitle } from './lib/formatTrendDrillOrdersSummary';
import {
	formatPinnedAtLabel,
	isFrozenChartSnapshot,
	isPageBoundChartSnapshot,
	resolveDashboardChartBindingLabel,
} from '../statistics/charts/board/lib/statisticsChartRefreshMode';
import { resolveStatisticsCreatedDateRangeLabel } from '../statistics/filters/lib/statisticsFilterTags';
import DashboardChartCommandPalette from './DashboardChartCommandPalette';
const canSavedChartDrillOrders = canDashboardChartDrillOrders;
const CHART_SPLIT_STORAGE_KEY = 'pringxsoft.dashboardChartModalSplitPct';
const CHART_SPLIT_MIN = 30;
const CHART_SPLIT_MAX = 70;
const CHART_SPLIT_DEFAULT = 50;

const readStoredChartSplitPct = () => {
	try {
		const raw = Number(window.localStorage.getItem(CHART_SPLIT_STORAGE_KEY));
		if (Number.isFinite(raw) && raw >= CHART_SPLIT_MIN && raw <= CHART_SPLIT_MAX) return raw;
	} catch {
		/* ignore quota / private mode */
	}
	return CHART_SPLIT_DEFAULT;
};

const resolveCatalogItems = (catalog = [], charts = []) => {
	if (catalog.length) return catalog;
	return charts.map((chart) => ({
		id: chart.id,
		title: chart.title,
		source: 'saved',
		snapshot: chart,
	}));
};

/** live-preset（外部传入 catalog 时的整卡组多图导航）钻取不可用说明；当前无调用方传 live-preset 项，保留以兼容该模式 */
const resolveLivePresetDrillDisabledReason = (_presetKey, { ordersCount = 0, ordersLoading = false } = {}) => {
	if (ordersLoading) return null;
	if (!ordersCount) {
		return STATISTICS_EMPTY_DRILL_ORDERS;
	}
	return null;
};

const DashboardChartsFullscreenModal = ({
	show,
	onHide,
	modalTitle = '经营看板',
	catalog = [],
	charts = [],
	activeChartId = null,
	onActiveChartChange,
	bundleByChartId = {},
	renderLivePreset,
	livePresetDrillEnabled,
	livePresetOrders = [],
	livePresetOrdersTotal = 0,
	livePresetOrdersLoading = false,
	useServerDrillOrders = false,
	isManager = false,
	showViewMoreLink = false,
	onPinToHome,
	pinToHomeDisabled = false,
	pinToHomeTitle = '固定口径到首页看板',
	pinnedToHome = false,
	pendingDrill = null,
	onPendingDrillApplied,
	onDrillChange,
	variant = 'modal',
}) => {
	useOrderFullscreenModalLayoutFix(show && variant === 'modal');

	const catalogItems = useMemo(() => resolveCatalogItems(catalog, charts), [catalog, charts]);
	const activeItem = useMemo(() => catalogItems.find((item) => item.id === activeChartId) ?? null, [activeChartId, catalogItems]);
	const isLivePreset = activeItem?.source === 'live-preset';
	const activeSnapshot = isLivePreset ? null : activeItem?.snapshot ?? charts.find((chart) => chart.id === activeChartId) ?? null;
	const activeBundle = !isLivePreset && activeChartId ? bundleByChartId[activeChartId] : null;
	const selectedPhase = activeSnapshot?.selectedPhase ?? null;

	const [showPointLabels, setShowPointLabels] = useState(true);
	const [drill, setDrill] = useState(null);
	const [recentChartIds, setRecentChartIds] = useState([]);
	const [chartSplitPct, setChartSplitPct] = useState(CHART_SPLIT_DEFAULT);
	const [isSplitDragging, setIsSplitDragging] = useState(false);
	const prevActiveChartIdRef = useRef(null);
	const splitRef = useRef(null);
	const chartSplitPctRef = useRef(CHART_SPLIT_DEFAULT);

	const savedDrillEnabled = canSavedChartDrillOrders(activeSnapshot, activeBundle);
	const liveDrillEnabled = isLivePreset ? Boolean(livePresetDrillEnabled?.(activeItem?.presetKey ?? activeChartId)) : false;
	const drillEnabled = isLivePreset ? liveDrillEnabled : savedDrillEnabled;

	const ordersLoading =
		(isLivePreset && livePresetOrdersLoading) ||
		(!isLivePreset &&
			Boolean(
				!activeBundle ||
					activeBundle?.trendChartLoading ||
					activeBundle?.chartLoading ||
					activeBundle?.isStatisticsQueryLoading ||
					activeBundle?.isStatisticsQueryFetching,
			));
	const displayDrill =
		show && (prevActiveChartIdRef.current == null || prevActiveChartIdRef.current === activeChartId) ? drill : null;

	const drillDisabledReason = useMemo(() => {
		if (drillEnabled) return null;
		if (isLivePreset) {
			return resolveLivePresetDrillDisabledReason(activeItem?.presetKey ?? activeChartId, {
				ordersCount: livePresetOrders.length,
				ordersLoading: livePresetOrdersLoading,
			});
		}
		return resolveSavedChartDrillDisabledReason(activeSnapshot, activeBundle, { ordersLoading });
	}, [
		activeBundle,
		activeChartId,
		activeItem?.presetKey,
		activeSnapshot,
		drillEnabled,
		isLivePreset,
		livePresetOrders.length,
		livePresetOrdersLoading,
		ordersLoading,
	]);

	useEffect(() => {
		if (!show) {
			prevActiveChartIdRef.current = null;
			setDrill(null);
			setRecentChartIds([]);
			return;
		}
		const storedPct = readStoredChartSplitPct();
		chartSplitPctRef.current = storedPct;
		setChartSplitPct(storedPct);
	}, [show]);

	useEffect(() => {
		if (!show) return;
		setShowPointLabels(activeSnapshot?.ui?.showPointLabels ?? true);
		const prevChartId = prevActiveChartIdRef.current;
		prevActiveChartIdRef.current = activeChartId;
		if (prevChartId != null && prevChartId !== activeChartId) {
			setRecentChartIds((current) => [prevChartId, ...current.filter((id) => id !== prevChartId)].slice(0, 5));
			setDrill(null);
			onDrillChange?.(null);
		}
	}, [activeChartId, activeSnapshot?.ui?.showPointLabels, onDrillChange, show]);

	useEffect(() => {
		if (!show || !pendingDrill) return;
		setDrill(pendingDrill);
		onDrillChange?.(pendingDrill);
		onPendingDrillApplied?.();
	}, [onDrillChange, onPendingDrillApplied, pendingDrill, show]);

	const notifyLayoutChange = useCallback(() => {
		requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
	}, []);

	const handleSplitPointerDown = useCallback((event) => {
		if (event.button !== 0) return;
		const splitEl = splitRef.current;
		if (!splitEl) return;
		event.preventDefault();
		const startX = event.clientX;
		const startPct = chartSplitPctRef.current;
		const width = splitEl.getBoundingClientRect().width || 1;
		setIsSplitDragging(true);
		let frame = 0;

		const onMove = (moveEvent) => {
			const next = Math.min(
				CHART_SPLIT_MAX,
				Math.max(CHART_SPLIT_MIN, startPct + ((moveEvent.clientX - startX) / width) * 100),
			);
			chartSplitPctRef.current = next;
			setChartSplitPct(next);
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				window.dispatchEvent(new Event('resize'));
			});
		};

		const onUp = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
			setIsSplitDragging(false);
			try {
				window.localStorage.setItem(CHART_SPLIT_STORAGE_KEY, String(Math.round(chartSplitPctRef.current)));
			} catch {
				/* ignore quota / private mode */
			}
			notifyLayoutChange();
		};

		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
	}, [notifyLayoutChange]);

	const handleDrillSelect = useCallback(
		(nextDrill) => {
			setDrill(nextDrill);
			onDrillChange?.(nextDrill);
		},
		[onDrillChange],
	);

	const handleClearDrill = useCallback(() => {
		setDrill(null);
		onDrillChange?.(null);
	}, [onDrillChange]);

	const drillOrdersLoading = Boolean(displayDrill) && isLivePreset && livePresetOrdersLoading;

	const drilledOrders = useMemo(() => {
		if (drillOrdersLoading) return [];
		if (isLivePreset) {
			return filterLivePresetDrillOrders(livePresetOrders, displayDrill);
		}
		return filterHomeDashboardChartDrillOrders(activeBundle?.orders ?? [], displayDrill, {
			selectedPhase,
		});
	}, [
		activeBundle?.orders,
		displayDrill,
		drillOrdersLoading,
		isLivePreset,
		livePresetOrders,
		selectedPhase,
	]);

	const ordersCount = isLivePreset ? livePresetOrders.length : activeBundle?.orders?.length ?? 0;
	const drilledOrdersCount = displayDrill
		? drillOrdersLoading
			? 0
			: useServerDrillOrders && livePresetOrdersTotal > 0
				? livePresetOrdersTotal
				: drilledOrders.length
		: ordersCount;

	const drillEmptyMessage = useMemo(() => {
		if (!displayDrill || drillOrdersLoading || ordersLoading) return null;
		if (drilledOrdersCount > 0) return null;
		return resolveDashboardChartDrillEmptyMessage(displayDrill);
	}, [displayDrill, drillOrdersLoading, drilledOrdersCount, ordersLoading]);

	const drillHintMessage = drillEnabled ? '点击图表数据点可查看对应订单明细' : drillDisabledReason;

	const isMapPreview = activeSnapshot ? resolvePersistedChartIsMapPreview(activeSnapshot, activeBundle) : false;
	const isOverdueChart = activeSnapshot ? resolvePersistedChartKind(activeSnapshot) === PERSISTED_CHART_KIND.OVERDUE : false;
	const isSavedChartLoading = !isLivePreset && isChartExplorerQueryLoading(activeBundle, activeSnapshot);
	const chartKindLabel = useMemo(
		() => (activeSnapshot ? resolvePersistedChartTypeLabel(activeSnapshot) : ''),
		[activeSnapshot],
	);

	const livePresetPane = useMemo(() => {
		if (!isLivePreset || !renderLivePreset) return null;
		const presetKey = activeItem?.presetKey ?? activeChartId;
		return renderLivePreset(presetKey, {
			height: '100%',
			enableOrderDrill: liveDrillEnabled,
			onDrillSelect: liveDrillEnabled ? handleDrillSelect : undefined,
		});
	}, [activeChartId, activeItem?.presetKey, handleDrillSelect, isLivePreset, liveDrillEnabled, renderLivePreset]);

	const drillOrdersAmountLabel = useMemo(() => {
		if (!displayDrill || drillOrdersLoading) return '';
		return formatLineCompareDrillOrdersAmount(drilledOrders);
	}, [displayDrill, drillOrdersLoading, drilledOrders]);

	const showOrdersPane = isLivePreset || Boolean(activeSnapshot);
	const showPointLabelsToggle =
		!isOverdueChart && resolvePersistedChartKind(activeSnapshot) !== PERSISTED_CHART_KIND.COMPOSITION;

	const compactMetaItems = useMemo(() => {
		if (!activeSnapshot) return [];
		const filters = buildPersistedChartFilters(activeSnapshot);
		const range = resolveStatisticsCreatedDateRangeLabel(filters.startDate, filters.endDate, filters.trendAnchor) || '不限';
		const items = [
			{ label: '范围', value: range },
			{ label: '口径', value: resolveDashboardChartBindingLabel(activeSnapshot).short },
		];
		if (isFrozenChartSnapshot(activeSnapshot) && activeSnapshot.pinnedAt) {
			items.push({ label: '快照', value: formatPinnedAtLabel(activeSnapshot.pinnedAt) });
		}
		return items;
	}, [activeSnapshot]);

	const credibility = useMemo(() => {
		if (!activeSnapshot) {
			return { mode: 'live', title: '● 实时同步', text: '数据随当前口径刷新' };
		}
		if (isFrozenChartSnapshot(activeSnapshot)) {
			return { mode: 'frozen', title: '■ 冻结快照', text: '数据已定格，不随最新业务变动，仅供对照与存档' };
		}
		if (isPageBoundChartSnapshot(activeSnapshot)) {
			return { mode: 'live', title: '● 实时同步', text: '跟统计页已应用筛选持续拉取，适合会议讨论' };
		}
		return { mode: 'caliber', title: '◆ 固定口径', text: '按保存时的筛选口径刷新，不随统计页当前筛选变化' };
	}, [activeSnapshot]);

	const isEmbedded = variant === 'embedded';

	const headerNode = isEmbedded ? (
		<header className='hdm-library-preview-header'>
			<div className='hdm-library-preview-header__main'>
				<h2 className='hdm-library-preview-header__title'>{activeSnapshot?.title ?? activeItem?.title ?? '图表预览'}</h2>
				{chartKindLabel ? <span className='hdm-library-preview-header__kind'>{chartKindLabel}</span> : null}
			</div>
			<div className='hdm-make-header__meta hdm-library-preview-header__meta'>
				{compactMetaItems.map((item, index) => (
					<span key={item.label} className='hdm-make-meta'>
						{index > 0 ? <span className='hdm-make-meta__rule'>│</span> : null}
						<span className='hdm-make-meta__k'>{item.label}</span>
						<span className='hdm-make-meta__v'>{item.value}</span>
					</span>
				))}
			</div>
			<div className='hdm-library-preview-header__actions'>
				{activeSnapshot ? <DashboardChartRefreshModeBadge snapshot={activeSnapshot} className='hdm-library-preview-header__live' /> : null}
				{typeof onPinToHome === 'function' && activeSnapshot ? (
					<button
						type='button'
						className='home-dashboard-charts-fullscreen-modal__pin-btn'
						onClick={() => onPinToHome(activeSnapshot)}
						disabled={pinToHomeDisabled}
						title={pinToHomeTitle}
					>
						{pinnedToHome ? '已在首页' : '固定至首页'}
					</button>
				) : null}
			</div>
		</header>
	) : (
		<Modal.Header closeButton className='hdm-make-header'>
			<Modal.Title className='visually-hidden'>{modalTitle}</Modal.Title>
			<DashboardChartCommandPalette
				items={catalogItems}
				activeId={activeChartId}
				onSelect={onActiveChartChange}
				recentIds={recentChartIds}
			/>
			<div className='hdm-make-header__meta'>
				{compactMetaItems.map((item, index) => (
					<span key={item.label} className='hdm-make-meta'>
						{index > 0 ? <span className='hdm-make-meta__rule'>│</span> : null}
						<span className='hdm-make-meta__k'>{item.label}</span>
						<span className='hdm-make-meta__v'>{item.value}</span>
					</span>
				))}
			</div>
			<div className='hdm-make-header__actions'>
				{showViewMoreLink ? (
					<Link to='/statistics' className='hdm-make-header__more' onClick={onHide}>
						查看更多
					</Link>
				) : null}
			</div>
		</Modal.Header>
	);

	const bodyInner = activeItem && (isLivePreset ? livePresetPane : activeSnapshot) ? (
		<div
			ref={splitRef}
			className={['hdm-make-split', !showOrdersPane ? 'is-chart-only' : null, isSplitDragging ? 'is-dragging' : null].filter(Boolean).join(' ')}
		>
			<div
				className={['hdm-make-chart', isLivePreset ? 'is-live' : null, isMapPreview ? 'is-map' : null].filter(Boolean).join(' ')}
				style={showOrdersPane ? { flex: `0 0 ${chartSplitPct}%`, width: `${chartSplitPct}%` } : undefined}
			>
				{!isEmbedded ? (
					<div className='hdm-make-chart__toolbar'>
						{activeSnapshot ? <DashboardChartRefreshModeBadge snapshot={activeSnapshot} /> : null}
						{typeof onPinToHome === 'function' && !isLivePreset ? (
							<button
								type='button'
								className='home-dashboard-charts-fullscreen-modal__pin-btn'
								onClick={() => onPinToHome(activeSnapshot)}
								disabled={pinToHomeDisabled || !activeSnapshot}
								title={pinToHomeTitle}
							>
								{pinnedToHome ? '已在首页' : '固定至首页'}
							</button>
						) : null}
						{showPointLabelsToggle ? (
							<label className='hdm-make-chart__labels'>
								<input type='checkbox' checked={showPointLabels} onChange={(event) => setShowPointLabels(event.target.checked)} />
								<span>显示数值</span>
							</label>
						) : null}
					</div>
				) : null}
				<div className='hdm-make-chart__plot'>
					{isLivePreset ? (
						livePresetPane
					) : isSavedChartLoading ? (
						<div className='hdm-make-empty' role='status' aria-live='polite' aria-label='加载图表'>
							<Spinner animation='border' size='sm' />
							<span>加载图表…</span>
						</div>
					) : (
						<DashboardChartView
							key={activeChartId}
							snapshot={activeSnapshot}
							chartBundle={activeBundle}
							height='100%'
							showPointLabels={showPointLabels}
							fillHeight
							fillPreview={isMapPreview}
							enableOrderDrill={drillEnabled}
							onDrillSelect={drillEnabled ? handleDrillSelect : undefined}
							preferProvidedBundle
						/>
					)}
				</div>
				{drillEnabled ? <p className='hdm-make-chart__hint'>{drillHintMessage}</p> : null}
			</div>

			{showOrdersPane ? (
				<>
					<div
						className='hdm-make-split__handle'
						role='separator'
						aria-orientation='vertical'
						aria-valuemin={CHART_SPLIT_MIN}
						aria-valuemax={CHART_SPLIT_MAX}
						aria-valuenow={Math.round(chartSplitPct)}
						aria-label='拖动调整图表与订单列表宽度'
						title='拖动调整宽度'
						onPointerDown={handleSplitPointerDown}
					/>
					<section
						className={['hdm-make-orders', ordersLoading ? 'stat-card-edge-pulse' : null].filter(Boolean).join(' ')}
						aria-label='订单明细'
						aria-busy={ordersLoading}
					>
						<div className='hdm-make-orders__head'>
							<span className='hdm-make-orders__title'>{displayDrill ? formatLineCompareDrillTitle(displayDrill) : '订单列表'}</span>
							<span className='hdm-make-orders__count'>
								<StatisticsCount value={drilledOrdersCount} loading={drillOrdersLoading || ordersLoading} suffix=' 笔' duration={0.35} />
							</span>
							{displayDrill ? (
								<button type='button' className='hdm-make-header__pin' onClick={handleClearDrill}>
									清除筛选
								</button>
							) : null}
						</div>
						<div className='hdm-make-orders__table'>
							<ChartExplorerOrdersTable
								key={activeChartId}
								orders={drilledOrders}
								isLoading={drillOrdersLoading || (ordersLoading && drilledOrders.length === 0)}
								emptyMessage={drillEmptyMessage ?? ''}
							/>
						</div>
					</section>
				</>
			) : null}
		</div>
	) : (
		<div className='hdm-make-empty'>
			<Spinner animation='border' size='sm' />
			<span>加载图表…</span>
		</div>
	);

	const shellContent = (
		<>
			{headerNode}
			<div className={`hdm-make-cred hdm-make-cred--${credibility.mode}`}>
				<strong>{credibility.title}</strong>
				<span>·</span>
				<span>{credibility.text}</span>
				{!drillEnabled && drillDisabledReason ? <span className='hdm-make-cred__blocked'>⊘ 此图表不支持钻取</span> : null}
			</div>

			{activeItem && (isLivePreset ? livePresetPane : activeSnapshot) ? (
				<div className='hdm-make-stats'>
					<div className='hdm-make-stat'>
						<div className='hdm-make-stat__k'>{displayDrill ? formatLineCompareDrillTitle(displayDrill) : '全部订单'}</div>
						<div className='hdm-make-stat__v'>
							<StatisticsCount value={drilledOrdersCount} loading={drillOrdersLoading || ordersLoading} suffix=' 单' duration={0.35} />
						</div>
					</div>
					{drillOrdersAmountLabel ? (
						<div className='hdm-make-stat'>
							<div className='hdm-make-stat__k'>合同金额</div>
							<div className='hdm-make-stat__v'>{drillOrdersAmountLabel}</div>
						</div>
					) : null}
					{chartKindLabel ? (
						<div className='hdm-make-stat'>
							<div className='hdm-make-stat__k'>图种</div>
							<div className='hdm-make-stat__v'>{chartKindLabel}</div>
						</div>
					) : null}
					{displayDrill ? (
						<div className='hdm-make-stats__drill'>
							<span className='hdm-make-stats__chip'>▸ {formatLineCompareDrillTitle(displayDrill)}</span>
							<button type='button' className='hdm-make-header__pin' onClick={handleClearDrill}>
								清除筛选
							</button>
						</div>
					) : null}
				</div>
			) : null}

			{isEmbedded ? <div className='hdm-make-body hdm-library-preview-body'>{bodyInner}</div> : <Modal.Body className='hdm-make-body'>{bodyInner}</Modal.Body>}
		</>
	);

	if (!show) return null;

	if (isEmbedded) {
		return <div className='hdm-library-preview-pane'>{shellContent}</div>;
	}

	return (
		<Modal
			show={show}
			onHide={onHide}
			fullscreen
			className='order-fullscreen-modal home-dashboard-charts-fullscreen-modal home-dashboard-charts-fullscreen-modal--make'
			dialogClassName='hdm-make-dialog'
			contentClassName='home-dashboard-charts-fullscreen-modal__content border-0'
			onEntered={notifyLayoutChange}
			onExited={notifyLayoutChange}
		>
			{shellContent}
		</Modal>
	);
};

DashboardChartsFullscreenModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func,
	modalTitle: PropTypes.string,
	variant: PropTypes.oneOf(['modal', 'embedded']),
	catalog: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			source: PropTypes.oneOf(['live-preset', 'saved']),
			presetKey: PropTypes.string,
			snapshot: PropTypes.object,
		}),
	),
	charts: PropTypes.arrayOf(PropTypes.object),
	activeChartId: PropTypes.string,
	onActiveChartChange: PropTypes.func,
	bundleByChartId: PropTypes.object,
	renderLivePreset: PropTypes.func,
	livePresetDrillEnabled: PropTypes.func,
	livePresetOrders: PropTypes.array,
	livePresetOrdersTotal: PropTypes.number,
	livePresetOrdersLoading: PropTypes.bool,
	useServerDrillOrders: PropTypes.bool,
	isManager: PropTypes.bool,
	showViewMoreLink: PropTypes.bool,
	onPinToHome: PropTypes.func,
	pinToHomeDisabled: PropTypes.bool,
	pinToHomeTitle: PropTypes.string,
	pinnedToHome: PropTypes.bool,
	pendingDrill: PropTypes.object,
	onPendingDrillApplied: PropTypes.func,
	onDrillChange: PropTypes.func,
};

export default memo(DashboardChartsFullscreenModal);
